import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, where, addDoc, setDoc, getDocs } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  User,
  MoreVertical,
  ArrowUpDown,
  X,
  MessageSquare,
  Users,
  UserCheck,
  History,
  Bot,
  Phone,
  Fingerprint,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';

export const AdminTickets: React.FC = () => {
  const { profile, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [statusUpdateSuccessId, setStatusUpdateSuccessId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  useEffect(() => {
    if (location.state && location.state.filterStatus) {
      setFilterStatus(location.state.filterStatus);
    }
  }, [location.state]);

  useEffect(() => {
    if (!profile || (profile.role !== 'admin' && profile.role !== 'staff')) return;

    const isPlatform = isPlatformAdmin(profile.email);
    const q = isPlatform 
      ? query(collection(db, 'tickets'), orderBy('createdAt', 'desc'))
      : query(
          collection(db, 'tickets'),
          where('tenantId', '==', profile.tenantId || ''),
          orderBy('createdAt', 'desc')
        );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tickets');
    });

    return () => unsubscribe();
  }, [profile]);

  const [users, setUsers] = useState<any[]>([]);

  // Fetch all users to list staff
  useEffect(() => {
    if (!profile || (profile.role !== 'admin' && profile.role !== 'staff')) return;
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Error fetching users for assignment:", error);
    });
    return () => unsubscribe();
  }, [profile]);

  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionSuccess, setConversionSuccess] = useState(false);

  const handleConvertToProject = async (ticket: any) => {
    if (!ticket) return;
    try {
      setConversionLoading(true);
      const projectRef = collection(db, 'projects');
      const timestamp = new Date().toISOString();
      
      const newProject = {
        title: ticket.subject.includes("Inquiry:") 
          ? ticket.subject.replace("Inquiry:", "").trim() 
          : ticket.subject.includes("Contact Form:") 
            ? ticket.subject.replace("Contact Form:", "").trim() 
            : ticket.subject,
        clientId: ticket.clientId || ticket.contact_id || ticket.clientEmail || 'unknown-client',
        clientEmail: ticket.clientEmail || '',
        clientName: ticket.clientName || '',
        clientPhone: ticket.clientPhone || '',
        description: ticket.message || ticket.description || '',
        progress: 10,
        role: ticket.project_type || 'Custom Services & Deliverables',
        deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        categories: [ticket.service_type || ticket.category || 'general'],
        tags: [ticket.priority || 'medium'],
        metrics: [
          { label: 'Project Phase', value: 'Discovery', description: 'Under design study' }
        ],
        details: {
          problem: ticket.message || ticket.description || '',
          strategy: 'Direct lead translation via operational control panel.',
          technical: 'React codebase with TypeScript modules & Firebase storage.',
          outcome: 'Lead successfully won and moved to Active Development queue.',
          testimonial: { quote: '', author: '', role: '' },
          takeaways: []
        },
        createdAt: timestamp,
        updatedAt: timestamp,
        tenantId: ticket.tenantId || ''
      };

      await addDoc(projectRef, newProject);

      // Now create a status history entry
      const historyEntry = {
        previous_status: ticket.status || 'qualified',
        new_status: 'won',
        changed_by: profile?.uid || 'system',
        changed_at: timestamp,
        note: 'Lead qualified and converted to active client project successfully.',
        // backward compatibility keys
        status: 'won',
        updatedAt: timestamp,
        changedByName: profile?.displayName || 'Admin Console'
      };

      const statusHistory = Array.isArray(ticket.status_history) ? [...ticket.status_history] : [];
      const legacyHistory = Array.isArray(ticket.statusHistory) ? [...ticket.statusHistory] : [];
      statusHistory.push(historyEntry);
      legacyHistory.push(historyEntry);

      const ticketRef = doc(db, 'tickets', ticket.id);
      await updateDoc(ticketRef, {
        status: 'won',
        status_history: statusHistory,
        statusHistory: legacyHistory,
        converted_to_client_at: timestamp,
        updated_at: timestamp,
        updatedAt: timestamp
      });

      // Update the loaded ticket state
      const updatedTicket = {
        ...ticket,
        status: 'won',
        status_history: statusHistory,
        statusHistory: legacyHistory,
        converted_to_client_at: timestamp,
        updated_at: timestamp,
        updatedAt: timestamp
      };

      setSelectedTicket(updatedTicket);
      setConversionSuccess(true);
      setTimeout(() => setConversionSuccess(false), 5000);
    } catch (error) {
      console.error("Error converting ticket to project:", error);
    } finally {
      setConversionLoading(false);
    }
  };

  const [clientAccountCreating, setClientAccountCreating] = useState(false);
  const [clientAccountSuccess, setClientAccountSuccess] = useState(false);
  const [clientAccountError, setClientAccountError] = useState<string | null>(null);

  const handleConvertToClientAccount = async (ticket: any) => {
    if (!ticket) return;
    try {
      setClientAccountCreating(true);
      setClientAccountError(null);
      setClientAccountSuccess(false);

      const clientEmail = ticket.clientEmail || ticket.clientId || '';
      if (!clientEmail) {
        throw new Error("Ticket is missing client email contact info.");
      }

      // Find an existing user with this email to avoid duplicate documents
      const existingUserQuery = query(collection(db, 'users'), where('email', '==', clientEmail.trim()));
      const querySnap = await getDocs(existingUserQuery);

      let userDocId = '';
      if (!querySnap.empty) {
        userDocId = querySnap.docs[0].id;
        // User already exists, update their role to 'client'
        const userRef = doc(db, 'users', userDocId);
        await updateDoc(userRef, {
          role: 'client'
        });
      } else {
        // Create new user collection record
        userDocId = ticket.clientId && !ticket.clientId.startsWith('anonymous-') 
          ? ticket.clientId 
          : doc(collection(db, 'users')).id;

        const userProfile = {
          uid: userDocId,
          email: clientEmail.trim(),
          displayName: ticket.clientName || 'Client Contact',
          role: 'client',
          createdAt: new Date().toISOString(),
          tenantId: ticket.tenantId || ''
        };

        await setDoc(doc(db, 'users', userDocId), userProfile);
      }

      // Create a new record in the 'clients' collection
      const clientRecord = {
        id: userDocId,
        userId: userDocId,
        email: clientEmail.trim(),
        name: ticket.clientName || 'Client Contact',
        phone: ticket.clientPhone || 'N/A',
        convertedFromTicket: ticket.id,
        createdAt: new Date().toISOString(),
        tenantId: ticket.tenantId || ''
      };
      await setDoc(doc(db, 'clients', userDocId), clientRecord);

      // Mark the original ticket status as 'won' with a corresponding status_history entry
      const prevStatus = ticket.status || 'new';
      const targetStatus = 'won';
      const timestamp = new Date().toISOString();

      const newHistoryEntry = {
        previous_status: prevStatus,
        new_status: targetStatus,
        changed_by: profile?.uid || 'system',
        changed_at: timestamp,
        note: `Convert to Client Action triggered: formal client profile initialized, user account generated/linked (${userDocId}), and ticket successfully converted.`,
        status: targetStatus,
        updatedAt: timestamp,
        changedByName: profile?.displayName || 'Admin Console'
      };

      const history = Array.isArray(ticket.status_history) ? [...ticket.status_history] : [];
      history.push(newHistoryEntry);

      const legacyHistory = Array.isArray(ticket.statusHistory) ? [...ticket.statusHistory] : [];
      legacyHistory.push(newHistoryEntry);

      // Update the ticket to link with the client account
      const ticketRef = doc(db, 'tickets', ticket.id);
      const updateData = {
        status: targetStatus,
        clientId: userDocId,
        converted_to_client_account_at: timestamp,
        status_history: history,
        statusHistory: legacyHistory,
        updated_at: timestamp,
        updatedAt: timestamp
      };

      await updateDoc(ticketRef, updateData);

      // Update the loaded ticket state
      const updatedTicket = {
        ...ticket,
        ...updateData
      };

      setSelectedTicket(updatedTicket);
      setClientAccountSuccess(true);
      setTimeout(() => setClientAccountSuccess(false), 5000);
    } catch (error: any) {
      console.error("Error converting ticket to client account:", error);
      setClientAccountError(error.message || "Failed to create client account.");
    } finally {
      setClientAccountCreating(false);
    }
  };

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      if (!ticket) return;

      const currentStatus = ticket.status || 'new';
      if (currentStatus === newStatus) return;

      let noteExtra = '';
      if (currentStatus.toLowerCase() === 'new' && (newStatus.toLowerCase() === 'qualified' || newStatus.toLowerCase() === 'active')) {
        noteExtra = ' (Pipeline auto-escalated: Lead converted/qualified from initial intake)';
      }

      const timestamp = new Date().toISOString();
      const newHistoryEntry = {
        previous_status: currentStatus,
        new_status: newStatus,
        changed_by: profile?.uid || 'system',
        changed_at: timestamp,
        note: `Status shifted from '${currentStatus}' to '${newStatus}'${noteExtra}`,
        // Backwards compatibility mappings
        status: newStatus,
        updatedAt: timestamp,
        changedByName: profile?.displayName || 'Admin Console'
      };

      const history = Array.isArray(ticket.status_history) ? [...ticket.status_history] : [];
      history.push(newHistoryEntry);

      const legacyHistory = Array.isArray(ticket.statusHistory) ? [...ticket.statusHistory] : [];
      legacyHistory.push(newHistoryEntry);

      const updateData: any = {
        status: newStatus,
        status_history: history,
        statusHistory: legacyHistory,
        updated_at: timestamp,
        updatedAt: timestamp
      };

      await updateDoc(doc(db, 'tickets', ticketId), updateData);

      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          ...updateData
        });
      }
      
      setStatusUpdateSuccessId(ticketId);
      setTimeout(() => setStatusUpdateSuccessId(null), 2500);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'tickets');
    }
  };

  const handlePriorityChange = async (ticketId: string, newPriority: string) => {
    try {
      const timestamp = new Date().toISOString();
      await updateDoc(doc(db, 'tickets', ticketId), {
        priority: newPriority,
        updated_at: timestamp,
        updatedAt: timestamp
      });
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          priority: newPriority,
          updated_at: timestamp,
          updatedAt: timestamp
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'tickets');
    }
  };

  const handleAssigneeChange = async (ticketId: string, assignedStaffId: string) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId) || selectedTicket;
      if (!ticket) return;

      const timestamp = new Date().toISOString();
      const staffMember = users.find(u => (u.uid === assignedStaffId || u.id === assignedStaffId));
      const staffName = staffMember ? (staffMember.displayName || staffMember.email) : 'Unassigned (General Queue)';

      const historyEntry = {
        previous_status: ticket.status || 'new',
        new_status: ticket.status || 'new',
        changed_by: profile?.uid || 'system',
        changed_at: timestamp,
        note: `Assignee updated: ticket assigned to ${staffName}.`,
        status: ticket.status || 'new',
        updatedAt: timestamp,
        changedByName: profile?.displayName || 'Admin Console'
      };

      const history = Array.isArray(ticket.status_history) ? [...ticket.status_history] : [];
      history.push(historyEntry);

      const legacyHistory = Array.isArray(ticket.statusHistory) ? [...ticket.statusHistory] : [];
      legacyHistory.push(historyEntry);

      const updateData = {
        assigned_staff_id: assignedStaffId || null,
        assignedTo: assignedStaffId || null,
        status_history: history,
        statusHistory: legacyHistory,
        updated_at: timestamp,
        updatedAt: timestamp
      };

      await updateDoc(doc(db, 'tickets', ticketId), updateData);
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          ...updateData
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'tickets');
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) || t.clientId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || t.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
  const currentTickets = filteredTickets.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ticket Management</h1>
          <p className="text-gray-400">Monitor and manage all client support requests across the platform.</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Search by subject or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-white/5 rounded-2xl pl-12 pr-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-all"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0A0A0A] border border-white/5 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-all appearance-none min-w-[140px] text-gray-300"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="in_review">In Review</option>
            <option value="qualified">Qualified</option>
            <option value="proposal_sent">Proposal Sent</option>
            <option value="won">Won (Deal Booked)</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5 text-xs font-mono text-gray-500 uppercase tracking-widest">
                <th className="px-6 py-4 font-medium">Client</th>
                <th className="px-6 py-4 font-medium">Subject</th>
                <th className="px-6 py-4 font-medium">Priority</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Created</th>
                <th className="px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentTickets.length > 0 ? (
                currentTickets.map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    onClick={() => setSelectedTicket(ticket)}
                    className="hover:bg-white/[0.04] cursor-pointer transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center border border-white/10">
                          <User size={14} className="text-gray-400" />
                        </div>
                        <span className="text-xs font-mono text-gray-500">#{ticket.clientId?.slice(0, 6) || 'ANON'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium">{ticket.subject || 'No Subject'}</p>
                      <p className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mt-0.5">{ticket.category || 'Uncategorized'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                        ticket.priority === 'urgent' ? 'text-red-500 bg-red-500/10' :
                        ticket.priority === 'high' ? 'text-orange-500 bg-orange-500/10' :
                        ticket.priority === 'medium' ? 'text-blue-500 bg-blue-500/10' :
                        'text-gray-500 bg-gray-500/10'
                      }`}>
                        {ticket.priority || 'medium'}
                      </span>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={ticket.status || 'new'}
                        onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                        className={`text-xs bg-[#0F0F0F] border border-white/5 rounded-xl px-2.5 py-1.5 cursor-pointer font-medium transition-all ${
                          ticket.status === 'new' ? 'text-cyan-400 border-cyan-400/20 bg-cyan-400/5' :
                          ticket.status === 'in_review' ? 'text-amber-400 border-amber-400/20 bg-amber-400/5' :
                          ticket.status === 'qualified' ? 'text-indigo-400 border-indigo-400/20 bg-indigo-400/5' :
                          ticket.status === 'proposal_sent' ? 'text-purple-400 border-purple-400/20 bg-purple-400/5' :
                          ticket.status === 'won' ? 'text-emerald-400 border-emerald-400/20 bg-emerald-400/5' :
                          ticket.status === 'active' ? 'text-blue-400 border-blue-400/20 bg-blue-400/5' :
                          'text-gray-400 border-white/5 bg-white/5'
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="in_review">In Review</option>
                        <option value="qualified">Qualified</option>
                        <option value="proposal_sent">Proposal Sent</option>
                        <option value="won">Won</option>
                        <option value="active">Active</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {ticket.createdAt ? format(new Date(ticket.createdAt), 'MMM d, HH:mm') : 'N/A'}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setSelectedTicket(ticket)}
                          className="p-2 text-gray-500 hover:text-white transition-colors bg-white/5 rounded-lg"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button className="p-2 text-gray-500 hover:text-white transition-colors">
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-gray-500 text-sm">
                    No tickets found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-white/5">
          {currentTickets.length > 0 ? (
            currentTickets.map((ticket) => (
              <div 
                key={ticket.id} 
                onClick={() => setSelectedTicket(ticket)}
                className="p-6 space-y-4 hover:bg-white/[0.04] cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center border border-white/10">
                      <User size={10} className="text-gray-400" />
                    </div>
                    <span className="text-[10px] font-mono text-gray-500">#{ticket.clientId?.slice(0, 6) || 'ANON'}</span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                    ticket.priority === 'urgent' ? 'text-red-500 bg-red-500/10' :
                    ticket.priority === 'high' ? 'text-orange-500 bg-orange-500/10' :
                    ticket.priority === 'medium' ? 'text-blue-500 bg-blue-500/10' :
                    'text-gray-500 bg-gray-500/10'
                  }`}>
                    {ticket.priority || 'medium'}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-bold mb-1">{ticket.subject || 'No Subject'}</h3>
                  <p className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">{ticket.category || 'Uncategorized'}</p>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <select
                    value={ticket.status || 'new'}
                    onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                    className={`text-[11px] bg-[#0F0F0F] border border-white/5 rounded-lg px-2.5 py-1.5 cursor-pointer font-medium transition-all ${
                      ticket.status === 'new' ? 'text-cyan-400 border-cyan-400/20 bg-cyan-400/5' :
                      ticket.status === 'in_review' ? 'text-amber-400 border-amber-400/20 bg-amber-400/5' :
                      ticket.status === 'qualified' ? 'text-indigo-400 border-indigo-400/20 bg-indigo-400/5' :
                      ticket.status === 'proposal_sent' ? 'text-purple-400 border-purple-400/20 bg-purple-400/5' :
                      ticket.status === 'won' ? 'text-emerald-400 border-emerald-400/20 bg-emerald-400/5' :
                      ticket.status === 'active' ? 'text-blue-400 border-blue-400/20 bg-blue-400/5' :
                      'text-gray-400 border-white/5 bg-white/5'
                    }`}
                  >
                    <option value="new">New</option>
                    <option value="in_review">In Review</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal_sent">Proposal Sent</option>
                    <option value="won">Won</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                  <span className="text-[10px] text-gray-500">
                    {ticket.createdAt ? format(new Date(ticket.createdAt), 'MMM d, HH:mm') : 'N/A'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-20 text-center text-gray-500 text-sm">
              No tickets found.
            </div>
          )}
        </div>
      </div>

      {/* Pagination component */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pt-4 flex-wrap">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-white/10 rounded-lg text-sm bg-[#0A0A0A] disabled:opacity-50 hover:bg-white/5 transition-all text-gray-300"
          >
            Prev
          </button>
          
          <div className="flex gap-1 overflow-x-auto max-w-[200px] sm:max-w-md scrollbar-hide">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              // Only show pages around current page, first, and last
              if (
                page === 1 || 
                page === totalPages || 
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`shrink-0 w-8 h-8 rounded-lg text-sm transition-all flex items-center justify-center ${
                      currentPage === page 
                        ? 'bg-blue-600 text-white font-bold' 
                        : 'bg-[#0A0A0A] border border-white/10 text-gray-400 hover:bg-white/5'
                    }`}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 2 || 
                page === currentPage + 2
              ) {
                return <span key={page} className="text-gray-500 flex items-center justify-center w-6">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-white/10 rounded-lg text-sm bg-[#0A0A0A] disabled:opacity-50 hover:bg-white/5 transition-all text-gray-300"
          >
            Next
          </button>
        </div>
      )}

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTicket(null)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl rounded-3xl overflow-hidden relative z-10 shadow-2xl"
            >
              <div className="p-8 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-3">
                    Ticket Details
                    <span className="text-xs font-mono text-gray-500 font-normal">#{selectedTicket.id.slice(0, 8)}</span>
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">Review and manage this support inquiry.</p>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="p-2 text-gray-500 hover:text-white bg-white/5 rounded-xl transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="p-8 space-y-8 h-auto overflow-y-auto max-h-[80vh] custom-scrollbar text-left">
                {selectedTicket.aiSummary && (
                  <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/5 border border-indigo-500/20 rounded-2xl p-5 text-left mb-6 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500/50"></div>
                    <div className="flex items-center gap-2 mb-3">
                      <Bot size={16} className="text-indigo-400 group-hover:scale-110 transition-transform" />
                      <h4 className="font-bold text-indigo-300 text-sm tracking-wide">Key Issues Summary (AI Generated)</h4>
                    </div>
                    <div className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed font-mono opacity-90 group-hover:opacity-100 transition-opacity">
                      {selectedTicket.aiSummary}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Subject / Issue</label>
                      <p className="text-lg font-medium">{selectedTicket.subject || 'No Subject'}</p>
                    </section>
                    
                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Client info</label>
                      <div className="flex flex-col gap-3 bg-white/5 p-4 rounded-2xl border border-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center border border-blue-500/20 shrink-0">
                            <User size={20} className="text-blue-500" />
                          </div>
                          <div className="overflow-hidden">
                            <p className="text-sm font-bold text-gray-200 truncate">
                              {selectedTicket.clientName ? selectedTicket.clientName : `User #${selectedTicket.clientId?.slice(0, 8) || 'ANON'}`}
                            </p>
                            {selectedTicket.clientId ? (
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Fingerprint size={10} className="text-gray-500 shrink-0" />
                                <span className="text-[10px] text-gray-500 font-mono truncate">ID: {selectedTicket.clientId}</span>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 font-mono truncate">No Client ID</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-2 pt-3 border-t border-white/5 mt-1">
                          {selectedTicket.clientEmail ? (
                            <div className="flex items-center justify-between group">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <Mail size={12} className="text-gray-500 shrink-0" />
                                <span className="text-xs text-gray-400 font-mono truncate">{selectedTicket.clientEmail}</span>
                              </div>
                              <a href={`mailto:${selectedTicket.clientEmail}`} className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-md text-[10px] font-mono text-gray-300 transition-colors flex items-center gap-1.5 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100" onClick={(e) => e.stopPropagation()}>
                                <Mail size={10} /> Email User
                              </a>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 overflow-hidden opacity-50">
                              <Mail size={12} className="text-gray-500 shrink-0" />
                              <span className="text-xs text-gray-400 font-mono truncate">No Email Provided</span>
                            </div>
                          )}
                          {selectedTicket.clientPhone && (
                            <div className="flex items-center justify-between group">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <Phone size={12} className="text-gray-500 shrink-0" />
                                <span className="text-xs text-gray-400 font-mono truncate">{selectedTicket.clientPhone}</span>
                              </div>
                              <a href={`tel:${selectedTicket.clientPhone}`} className="px-2 py-1 bg-white/5 hover:bg-white/10 rounded-md text-[10px] font-mono text-gray-300 transition-colors flex items-center gap-1.5 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100" onClick={(e) => e.stopPropagation()}>
                                <Phone size={10} /> Call User
                              </a>
                            </div>
                          )}
                        </div>

                        <div className="bg-blue-500/5 p-3 rounded-xl border border-blue-500/10 mt-1 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <History size={14} className="text-blue-400" />
                            <span className="text-xs text-blue-200">Previous Interactions</span>
                          </div>
                          <span className="text-[11px] font-mono font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md border border-blue-500/20">
                            {tickets.filter(t => (t.clientId === selectedTicket.clientId || (t.clientEmail && t.clientEmail === selectedTicket.clientEmail)) && t.id !== selectedTicket.id).length}
                          </span>
                        </div>
                      </div>
                    </section>

                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Assigned Staff Member</label>
                      <div className="relative">
                        <Users size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select
                          value={selectedTicket.assigned_staff_id || selectedTicket.assignedTo || ''}
                          onChange={(e) => handleAssigneeChange(selectedTicket.id, e.target.value)}
                          className="w-full bg-[#0E0E0E] border border-white/10 rounded-2xl pl-12 pr-4 py-3 text-sm font-bold text-gray-200 focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer"
                        >
                          <option value="">Unassigned (General Queue)</option>
                          {users
                            .filter(u => u.role === 'admin' || u.role === 'staff')
                            .map(staff => (
                              <option key={staff.uid || staff.id} value={staff.uid || staff.id}>
                                {staff.displayName || staff.email} ({staff.role})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    </section>
                  </div>

                  <div className="space-y-6">
                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Priority Levels</label>
                      <select
                        value={selectedTicket.priority || 'medium'}
                        onChange={(e) => handlePriorityChange(selectedTicket.id, e.target.value)}
                        className={`w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm font-bold transition-all focus:outline-none focus:border-blue-500/50 cursor-pointer ${
                          selectedTicket.priority === 'urgent' ? 'text-red-500' :
                          selectedTicket.priority === 'high' ? 'text-orange-500' :
                          selectedTicket.priority === 'medium' ? 'text-blue-500' :
                          'text-gray-400'
                        }`}
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent Priority</option>
                      </select>
                    </section>

                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Primary Category</label>
                      <span className="text-xs font-bold uppercase tracking-wider block px-4 py-3.5 rounded-2xl text-gray-400 bg-white/5 border border-white/10">
                        {selectedTicket.category || 'Uncategorized'}
                      </span>
                    </section>

                    <section>
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-2 block">Status Management & Pipeline</label>
                      <div className="relative">
                        <select
                          value={selectedTicket.status || 'new'}
                          onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                          className={`w-full bg-white/5 border rounded-2xl px-4 py-3 text-sm font-bold transition-all focus:outline-none cursor-pointer ${
                            selectedTicket.status === 'new' ? 'text-cyan-400' :
                            selectedTicket.status === 'in_review' ? 'text-amber-400' :
                            selectedTicket.status === 'qualified' ? 'text-indigo-400' :
                            selectedTicket.status === 'proposal_sent' ? 'text-purple-400' :
                            selectedTicket.status === 'won' ? 'text-emerald-400' :
                            selectedTicket.status === 'active' ? 'text-blue-400' :
                            'text-gray-400'
                          } ${
                            statusUpdateSuccessId === selectedTicket.id 
                              ? 'border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)] bg-green-500/10' 
                              : 'border-white/10 focus:border-blue-500/50'
                          }`}
                        >
                          <option value="new">New Lead</option>
                          <option value="in_review">In Review</option>
                          <option value="qualified">Qualified</option>
                          <option value="proposal_sent">Proposal Sent</option>
                          <option value="won">Won (Deal Booked)</option>
                          <option value="active">Active Workspace</option>
                          <option value="closed">Closed</option>
                        </select>
                        <AnimatePresence>
                          {statusUpdateSuccessId === selectedTicket.id && (
                            <motion.div 
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none"
                            >
                              <CheckCircle2 size={18} className="text-green-500" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </section>
                  </div>
                </div>

                {/* Message / Description Body Section */}
                {(selectedTicket.message || selectedTicket.description) && (
                  <div className="border-t border-white/5 pt-6 text-left">
                    <label className="text-[10px] font-mono text-gray-500 uppercase tracking-[0.2em] mb-3 block">Message / Inquiry Details</label>
                    <div className="bg-white/[0.01] border border-white/5 p-5 rounded-2xl text-gray-300 text-sm whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto font-mono">
                      {selectedTicket.message || selectedTicket.description}
                    </div>
                  </div>
                )}

                {/* Convert to Client Section */}
                {(selectedTicket.status === 'qualified' || selectedTicket.status === 'won' || selectedTicket.status === 'active') && (
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-2xl p-5 space-y-3 text-left">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <UserCheck size={18} className="text-green-400" />
                        <h4 className="font-bold text-green-400 text-sm">CRM Actions: Convert to Client</h4>
                      </div>
                      {selectedTicket.converted_to_client_at && (
                        <span className="text-[10px] font-mono bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                          CONVERTED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      Convert this qualified lead to an active project dashboard. This will generate a formal client engagement contract record automatically.
                    </p>
                    <button
                      onClick={() => handleConvertToProject(selectedTicket)}
                      disabled={conversionLoading || !!selectedTicket.converted_to_client_at}
                      className="w-full sm:w-auto px-6 py-2.5 bg-green-600 hover:bg-green-500 text-black font-bold text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {conversionLoading ? (
                        'Generating Project...'
                      ) : selectedTicket.converted_to_client_at ? (
                        <>
                          <UserCheck size={14} /> Account Promoted Successfully
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={14} /> Promote to Active Client Account
                        </>
                      )}
                    </button>
                    {conversionSuccess && (
                      <p className="text-xs font-mono text-green-400 mt-2">
                        🎉 Account promoted and project created successfully!
                      </p>
                    )}
                  </div>
                )}

                {/* Provision Client's User Account in Database */}
                <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/5 border border-blue-500/20 rounded-2xl p-5 space-y-3 text-left">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <UserCheck size={18} className="text-blue-400" />
                      <h4 className="font-bold text-blue-400 text-sm">CRM Actions: Client Account Provisioning</h4>
                    </div>
                    {selectedTicket.converted_to_client_account_at && (
                      <span className="text-[10px] font-mono bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                        PROVISIONED
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    Instantiate a formal client profile record in the central users directory. This allows the client to securely access their active project resources.
                  </p>
                  <button
                    onClick={() => handleConvertToClientAccount(selectedTicket)}
                    disabled={clientAccountCreating || !(selectedTicket.status?.toLowerCase() === 'qualified' || selectedTicket.status?.toLowerCase() === 'won')}
                    className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-white/5 disabled:hover:bg-white/5 disabled:text-gray-500 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {clientAccountCreating ? (
                      'Provisioning Account...'
                    ) : selectedTicket.converted_to_client_account_at ? (
                      <>
                        <UserCheck size={14} /> Convert to Client (Re-provision)
                      </>
                    ) : (
                      <>
                        <UserCheck size={14} /> Convert to Client
                      </>
                    )}
                  </button>
                  {clientAccountSuccess && (
                    <p className="text-xs font-mono text-green-400 mt-2">
                      🎉 Client account document created and linked successfully!
                    </p>
                  )}
                  {clientAccountError && (
                    <p className="text-xs font-mono text-red-500 mt-2">
                      ⚠️ {clientAccountError}
                    </p>
                  )}
                </div>

                {/* Status Timeline History */}
                <div className="border-t border-white/5 pt-6 space-y-4 text-left">
                  <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                    <History size={16} className="text-blue-400" />
                    Status History & Pipeline Transition Audit
                  </h3>
                  <div className="bg-white/[0.01] border border-white/5 rounded-2xl p-5 space-y-4 max-h-56 overflow-y-auto">
                    {((selectedTicket.status_history && selectedTicket.status_history.length > 0) || (selectedTicket.statusHistory && selectedTicket.statusHistory.length > 0)) ? (
                      <div className="space-y-4 relative before:absolute before:left-3 before:top-2 before:bottom-2 before:w-[1px] before:bg-white/10">
                        {(() => {
                          const rawList = [...(selectedTicket.status_history || []), ...(selectedTicket.statusHistory || [])];
                          const seen = new Set();
                          const uniqueList: any[] = [];
                          for (let i = rawList.length - 1; i >= 0; i--) {
                            const entry = rawList[i];
                            const sVal = entry.new_status || entry.status || 'new';
                            const dVal = entry.changed_at || entry.updatedAt || '';
                            const key = `${sVal}-${dVal}`;
                            if (!seen.has(key)) {
                              seen.add(key);
                              uniqueList.unshift(entry);
                            }
                          }
                          return uniqueList.map((item: any, i: number) => {
                            const stepStatus = item.new_status || item.status || 'new';
                            const stepDate = item.changed_at || item.updatedAt || new Date().toISOString();
                            const stepName = item.changedByName || (item.changed_by === 'system' ? 'System Inbound' : 'Admin Console');
                            
                            const formatLabel = (s: string) => {
                              if (!s) return '';
                              return s.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                            };
                            
                            const prevLabel = formatLabel(item.previous_status);
                            const newLabel = formatLabel(stepStatus);
                            const hasTransition = prevLabel && prevLabel !== newLabel;

                            return (
                              <div key={i} className="flex gap-4 relative pl-8">
                                <div className="absolute left-[7px] top-[5px] w-[11px] h-[11px] rounded-full bg-blue-500 border border-black" />
                                <div className="flex-1 space-y-1 flex flex-col items-start text-left">
                                  <div className="flex items-center justify-between gap-4 w-full">
                                    <span className="text-xs uppercase font-mono tracking-wider font-bold text-blue-400">
                                      {hasTransition ? (
                                        <>
                                          <span className="text-gray-500 font-medium">{prevLabel}</span>
                                          <span className="text-gray-400 mx-1.5 font-normal">→</span>
                                          <span>{newLabel}</span>
                                        </>
                                      ) : (
                                        <span>{newLabel}</span>
                                      )}
                                    </span>
                                    <span className="text-[10px] font-mono text-gray-500 text-right">
                                      {format(new Date(stepDate), 'MMM d, yyyy HH:mm')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-300">{item.note || 'No transaction note supplied.'}</p>
                                  <p className="text-[10px] text-gray-500 font-mono">
                                    Actioned by: <span className="text-gray-400">{stepName}</span>
                                  </p>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-4">No historical transitions recorded for this ticket.</p>
                    )}
                  </div>
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => {
                      navigate(`/admin/messages?ticketId=${selectedTicket.id}`);
                    }}
                    className="flex-1 flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20 active:scale-95 group"
                  >
                    <MessageSquare size={20} className="group-hover:scale-110 transition-transform" />
                    Open Conversation
                  </button>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="flex-1 bg-white/5 hover:bg-white/10 text-gray-300 font-bold py-4 rounded-2xl border border-white/10 transition-all active:scale-95"
                  >
                    Close View
                  </button>
                </div>
              </div>

              <div className="p-4 bg-white/[0.02] border-t border-white/5 text-center">
                <p className="text-[10px] font-mono text-gray-600 uppercase tracking-widest">
                  Created on {selectedTicket.createdAt ? format(new Date(selectedTicket.createdAt), 'MMMM d, yyyy HH:mm') : 'N/A'}
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
